/**
 * Table scope — which SKU rows the ladder table lists — and the one row that
 * is lifted out of it entirely (`partitionUnattributed`).
 *
 * ── Why this exists ──
 *
 * `/v1/ops/business/sku-economics` answers with EVERY priced SKU, not only
 * the ones that saw traffic. In production that is ~50 rows, and the table is
 * as tall as the catalog no matter which window is selected, so the default
 * view would be dominated by catalog entries that did nothing during the
 * window an operator is actually looking at.
 *
 * ── The invariant this module must not break ──
 *
 * Scope narrows the TABLE and nothing else. `SummaryStrip` keeps reading the
 * unscoped `PerspectiveModel`, so 综合毛利率 / 错误率 / 未挂链路 / 单点供应 /
 * 不健康上游 stay whole-fleet figures under every scope. That is not a
 * stylistic preference: a filter that silently moved the headline margin
 * would be the same class of error as printing 0% for an unsettled window —
 * a number that is arithmetically fine and means something the operator did
 * not ask about. The page states the rule in the control's own label
 * (`scope.count` names 表格 / "Table") and repeats it in `scope.note`.
 *
 * For the same reason `NodeInspector` is also given the UNSCOPED model: a
 * `?node=` deep link must still resolve when the node is outside the current
 * scope, and the ladder/divergence lookups need every link.
 */

import type {
  NamespaceNode,
  PerspectiveModel,
  SkuNode,
  SupplyChannelNode,
  UpstreamModelNode,
} from './types.ts';

export const PERSPECTIVE_SCOPES = ['traffic', 'problems', 'all'] as const;
export type PerspectiveScope = (typeof PERSPECTIVE_SCOPES)[number];

export interface PartitionedModel {
  /** Only real SKUs. What the ladder table lists and the scope count counts. */
  readonly model: PerspectiveModel;
  /** The `skuId === null` bucket, if the window produced one. */
  readonly unattributed: SkuNode | null;
}

/**
 * Lift the `skuId === null` bucket out of the SKU list.
 *
 * ── Why it cannot stay a table row ──
 *
 * Production, 24h, is four unrelated things sharing one row:
 *
 *   /v1/messages/count_tokens  completed 200   2,698   free, local estimate
 *   /v1/chat/completions       failed    402     758   balance refusals
 *   /v1/responses              failed    404     289   model not available
 *   /v1/responses             started   NULL      52   still in flight
 *
 * The bulk is `count_tokens`, which this repo answers from a local estimate
 * and never forwards or bills (CLAUDE.md). It correctly has no SKU — it is
 * not a fault and not a product. Ranking it as a SKU row is wrong three
 * ways: it is not a SKU, its ~2,700 free calls dilute a downstream failure
 * rate that is supposed to describe paid serving, and 严重 21.9% on it tells
 * an operator nothing they can act on. It also has no routing targets, so it
 * is a permanently empty row in a table whose whole subject is ladders.
 *
 * It is NOT dropped — the two failure groups inside it are real findings
 * (paying customers refused for balance; customers asking for a model we do
 * not serve on that ingress) — it is rendered by `UnattributedPanel`, which
 * states what this data can and cannot say about the composition.
 *
 * The KPI strip keeps reading the UNPARTITIONED model, so every headline
 * figure still covers the whole fleet including these requests.
 */
export function partitionUnattributed(model: PerspectiveModel): PartitionedModel {
  const unattributed = model.skus.find((sku) => sku.skuId === null) ?? null;
  if (unattributed === null) return { model, unattributed: null };
  return {
    model: { ...model, skus: model.skus.filter((sku) => sku.skuId !== null) },
    unattributed,
  };
}

/**
 * "What is happening right now" is what the page is opened for, so the
 * traffic scope is the default. Unknown / absent narrows here; never throws
 * (same idiom as `pages/metrics/hubState.ts`).
 */
export const DEFAULT_SCOPE: PerspectiveScope = 'traffic';

export function parseScope(raw: string | null): PerspectiveScope {
  return PERSPECTIVE_SCOPES.find((scope) => scope === raw) ?? DEFAULT_SCOPE;
}

/**
 * Does this SKU belong in the given scope?
 *
 * `problems` is deliberately a UNION rather than a grade threshold: a SKU
 * that is `unrouted` has no traffic to grade at all and would score `idle`,
 * yet it is the single most actionable row on the page. Same for
 * `single_point` (one upstream away from an outage) and any non-zero
 * fallback rate (the ladder is firing, which is a supply signal even while
 * tenants are still being served).
 */
function inScope(sku: SkuNode, scope: PerspectiveScope): boolean {
  if (scope === 'all') return true;
  if (scope === 'traffic') return sku.traffic.requests > 0;
  return (
    sku.grade === 'warn' ||
    sku.grade === 'bad' ||
    sku.coverageStatus === 'unrouted' ||
    sku.coverageStatus === 'single_point' ||
    sku.traffic.fallbackRate > 0
  );
}

export interface ScopedModel {
  /** The narrowed model — for the TABLE only. Never for the KPI strip. */
  readonly model: PerspectiveModel;
  readonly shownSkuCount: number;
  readonly totalSkuCount: number;
}

/**
 * Derive the table's model. Everything is rebuilt as new objects; the input
 * model is never mutated.
 */
export function scopeModel(model: PerspectiveModel, scope: PerspectiveScope): ScopedModel {
  const totalSkuCount = model.skus.length;
  if (scope === 'all') {
    return { model, shownSkuCount: totalSkuCount, totalSkuCount };
  }

  const skus = model.skus.filter((sku) => inScope(sku, scope));
  const keptSkuIds = new Set(skus.map((sku) => sku.id));
  const links = model.links.filter((link) => keptSkuIds.has(link.fromId));
  const linkedTargetIds = new Set(links.map((link) => link.toId));
  const upstreamModels: readonly UpstreamModelNode[] = model.upstreamModels.filter((target) =>
    linkedTargetIds.has(target.id),
  );

  // Channels are the MEASURED supply grain, so they carry the rescue arm: a
  // channel survives if a kept ladder rung lands on one of its models — or if
  // it has measured traffic while no model anywhere in the full model points
  // back at it. That second arm is the unattributed / off-pool bucket, which
  // no SKU filter can ever surface; without it, real measured supply would be
  // permanently invisible in every scope but 全部. Dropping measured supply
  // silently is exactly what this page must not do.
  const keptChannelIds = new Set(
    upstreamModels
      .map((target) => target.channelNodeId)
      .filter((id): id is string => id !== null),
  );
  const modelledChannelIds = new Set(
    model.upstreamModels
      .map((target) => target.channelNodeId)
      .filter((id): id is string => id !== null),
  );
  const channels: readonly SupplyChannelNode[] = model.channels.filter(
    (channel) =>
      keptChannelIds.has(channel.id) ||
      (channel.traffic.requests > 0 && !modelledChannelIds.has(channel.id)),
  );

  // Namespaces are pure aggregates of their SKUs, so they follow: the
  // membership list is narrowed to the kept SKUs and an emptied namespace
  // drops out rather than lingering as a heading with no rows.
  //
  // `skuIds` is matched in BOTH forms — node id (`sku:x`) and raw SKU id —
  // because the model documents it as carrying either. Matching only one form
  // would quietly empty every namespace the day the builder switched.
  const namespaces = model.namespaces
    .map<NamespaceNode>((namespace) => ({
      ...namespace,
      skuIds: namespace.skuIds.filter(
        (skuId) => keptSkuIds.has(skuId) || keptSkuIds.has(`sku:${skuId}`),
      ),
    }))
    .filter((namespace) => namespace.skuIds.length > 0);

  return {
    model: { ...model, namespaces, skus, channels, upstreamModels, links },
    shownSkuCount: skus.length,
    totalSkuCount,
  };
}
